import { storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc';
import StoryblokRichText from '@/components/StoryblokRichText';
import CmsButton from '@/components/CmsButton';
import PhosphorIcon from '@/components/PhosphorIcon';
import { colorStyle, hoverColorStyle, styleOverrides } from '@/lib/helpers';

const sizeMap = { '24': 24, '32': 32, '40': 40, '48': 48 };

const Card = ({ blok }) => {
  // Large is the old site's feature-card type: the description is the card's
  // whole message, set at 24px rather than as small body copy.
  const descSize = blok.description_size === 'large'
    ? 'text-20 sm:text-24 prose-p:text-20 sm:prose-p:text-24 prose-p:leading-[1.3]'
    : 'text-14 sm:text-16';
  // White is the default card background; an editor pick overrides it.
  // Hover/focus colours ride along as custom properties plus a utility class per
  // field that is set (see app/styles/hover-colors.css) — they also light up
  // while this card is the selected option of a Toggle.
  const hover = hoverColorStyle(blok);
  const style = { ...colorStyle(blok, { into: { backgroundColor: '#FFFFFF' } }), ...styleOverrides(blok), ...hover.style };
  // h-full so cards fill their grid cell (equal heights), incl. when wrapped in a Link.
  const className = `cms-card ${blok.border_radius || 'rounded-[0.5rem]'} p-6 sm:p-8 shadow-card flex flex-col gap-4 h-full ${hover.className}`.trim();

  return (
    <div {...storyblokEditable(blok)} className={className} style={style}>
      {blok.badge && (
        <span className="text-12 font-bold uppercase tracking-wider">{blok.badge}</span>
      )}
      {blok.icon && (
        <PhosphorIcon
          name={blok.icon}
          size={sizeMap[blok.icon_size] || 32}
          color={blok.icon_color || undefined}
        />
      )}
      {blok.title && (
        <h3 className="text-20 sm:text-24 font-bold">{blok.title}</h3>
      )}
      {blok.description && (
        typeof blok.description === 'string'
          ? <p className={descSize}>{blok.description}</p>
          : blok.description?.content?.[0]?.content && (
            <StoryblokRichText content={blok.description} className={descSize} />
          )
      )}
      {blok.body?.map((b) => <StoryblokServerComponent blok={b} key={b._uid} />)}
      {blok.cta_text && (
        <CmsButton label={blok.cta_text} link={blok.cta_link} style={blok.cta_style} showArrow={blok.cta_arrow} fullWidth={blok.cta_full_width} className="mt-auto" />
      )}
    </div>
  );
};

export default Card;
